import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Linkedin, Github, Check, GraduationCap, Briefcase, Maximize2, X, ExternalLink } from 'lucide-react';
import { TelegramIcon } from '../components/icons/TelegramIcon';
import { GlassCard } from '../components/LiquidComponents';
import { founderLinks } from '../constants/founderLinks';
import { useData } from '../contexts/DataContext';
import { lockScroll, unlockScroll } from '../services/scrollLock';

// Custom X (Twitter) Logo Component
const XLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor" width="20" height="20">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// --- Core Competencies ------------------------------------------------------
// Source of truth for this section is code, not the DB. We render an ordered
// set of skill groups defined below; profile.skills is intentionally no longer
// consulted here. Signature skills weight as solid white pills for hierarchy
// and match by exact string (with a defensive trim on compare).
const COMPETENCY_GROUPS: { label: string; skills: string[]; fullWidth?: boolean }[] = [
  {
    label: 'Strategy & Leadership',
    skills: [
      'Strategic Planning',
      'Leadership',
      'Business Management',
      'Company Building / 0-to-1',
      'Execution / Operations',
      'Resource Allocation',
      'Decision-Making Under Uncertainty',
      'Cross-Functional Leadership',
      'Team Building',
      'Time Management',
      'Bootstrapping',
    ],
  },
  {
    label: 'Ventures & Ecosystem',
    skills: [
      'Venture Capital',
      'Startup Ecosystem',
      'Fundraising / Capital Formation',
      'Investor Relations',
      'Pitching',
      'Due Diligence',
      'Ecosystem Partnerships',
    ],
  },
  {
    label: 'Product & Technology',
    skills: [
      'Product Management',
      'Product Strategy / Roadmapping',
      'AI/ML',
      'AI Product Development',
      'Web3 / Decentralized Systems',
      'Tokenomics',
      'Digital Identity',
      'Blockchain Domain Knowledge',
      'UX / Product Design Sense',
      'Go-to-Market Strategy',
      'Coding (Basic)',
    ],
  },
  {
    label: 'Communication & Brand',
    skills: [
      'Negotiation Skills',
      'Diplomacy',
      'Intercultural Communication',
      'Brand Building',
      'Content / Narrative',
      'Writing',
      'Market Research / Analysis',
      'Psychology',
    ],
  },
  {
    // Languages reads as a clean final band: with 5 groups in a 2-col grid it
    // would otherwise sit alone beside an empty half-cell, so it spans both
    // columns and closes the section as one full-width row.
    label: 'Languages',
    fullWidth: true,
    skills: ['English', 'Russian', 'Azerbaijani', 'Turkish', 'German'],
  },
];

// Signature competencies get weighted as solid white pills for hierarchy.
const SIGNATURE_SKILLS = new Set<string>([
  'Company Building / 0-to-1',
  'Fundraising / Capital Formation',
  'AI Product Development',
  'Web3 / Decentralized Systems',
  'Digital Identity',
]);

// Pill styles — quiet pill mirrors the detail page's Focus Areas chips;
// signature pill inverts to solid white (monochrome, no blue). Both carry a
// 1px border so the two variants keep identical box height when wrapped.
const QUIET_PILL =
  'px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap bg-black/[0.04] dark:bg-white/[0.05] [.nardogrey_&]:bg-white/[0.05] border border-black/5 dark:border-white/10 [.nardogrey_&]:border-white/10 text-zinc-600 dark:text-zinc-300 [.nardogrey_&]:text-zinc-300';
const SIGNATURE_PILL =
  'px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap font-medium bg-white text-zinc-900 border border-white [.nardogrey_&]:bg-white [.nardogrey_&]:text-zinc-900';

export const About: React.FC = () => {
  const { profile, loading } = useData();
  
  const [emailCopied, setEmailCopied] = useState(false);
  const [isBioOpen, setIsBioOpen] = useState(false);

  const fullBiography = profile?.bio || "Biography loading...";

  const handleCopyEmail = () => {
    if (profile?.email) {
      navigator.clipboard.writeText(profile.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    }
  };

  const handleInvestmentAction = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new Event('open-contact-modal'));
  };

  // The "Full Biography" modal now routes through the shared scroll lock (like the
  // other modals) so it inherits the Lenis stop/start cooperation and freezes the
  // page behind it. Its scrollable body carries data-lenis-prevent below.
  useEffect(() => {
    if (!isBioOpen) return undefined;
    lockScroll();
    return () => unlockScroll();
  }, [isBioOpen]);

  // Hide the floating chat launcher while the biography modal is open, reusing
  // the ChatWidget's modal-opened/modal-closed mechanism.
  useEffect(() => {
    if (!isBioOpen) return undefined;
    window.dispatchEvent(new Event('modal-opened'));
    return () => {
      window.dispatchEvent(new Event('modal-closed'));
    };
  }, [isBioOpen]);

  if (loading || !profile) {
    return (
      <div className="min-h-screen pt-32 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-500"></div>
      </div>
    );
  }

  return (
    <div className="pt-36 md:pt-44 pb-24 min-h-screen container mx-auto px-4 md:px-6 max-w-7xl">
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column (Profile & CTA) */}
        <div className="lg:col-span-4 space-y-6 h-fit lg:sticky lg:top-32">
          
          {/* Profile Card — hoverScale disabled: card stays static on hover (no zoom/jitter) */}
          <GlassCard hoverScale={false} className="p-6 md:p-8 transform-gpu will-change-transform backface-hidden">
             {/* Identity — centered (avatar / name / role / handle) */}
             <div className="flex flex-col items-center text-center">
               {/* Avatar — existing image kept exactly as-is, presented crisply */}
               <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden ring-1 ring-black/10 dark:ring-white/15 shadow-[0_10px_30px_-6px_rgba(0,0,0,0.35)]">
                 {profile.avatarUrl ? (
                   <img src={profile.avatarUrl} alt="Ruhlan Jafarli" className="w-full h-full object-cover" />
                 ) : (
                   <img src="/favicon-512x512.png" alt="" className="w-full h-full object-cover" />
                 )}
               </div>
               <h1 className="mt-6 text-xl md:text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">{profile.name}</h1>
               <p className="mt-1.5 text-sm font-light text-zinc-600 dark:text-zinc-300">{(profile as any).role || 'Serial Solo Founder'}</p>
               <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500">{profile.nickname}</p>
             </div>
             
             {/* Divider — intentional break before the connect row */}
             <div className="my-7 border-t border-black/5 dark:border-white/5" />

             {/* Connect — one row of icon-only links: socials + writing/profile brands
                 (footer treatment: light zinc, near-white on hover, subtle lift) */}
             <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
               {profile.socials?.linkedin && (
                 <a href={profile.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 hover:-translate-y-0.5"><Linkedin size={23} strokeWidth={1.75} /></a>
               )}
               {profile.socials?.twitter && (
                 <a href={profile.socials.twitter} target="_blank" rel="noreferrer" aria-label="X" className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 hover:-translate-y-0.5"><XLogo className="w-[21px] h-[21px]" /></a>
               )}
               {profile.socials?.github && (
                 <a href={profile.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub" className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 hover:-translate-y-0.5"><Github size={23} strokeWidth={1.75} /></a>
               )}
               {profile.socials?.telegram && (
                 <a href={profile.socials.telegram} target="_blank" rel="noreferrer" aria-label="Telegram" className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 hover:-translate-y-0.5"><TelegramIcon size={22} /></a>
               )}
               {founderLinks.map((link) => (
                 <a key={link.id} href={link.href} target="_blank" rel="noreferrer" aria-label={link.label} title={link.label} className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 hover:-translate-y-0.5"><link.icon size={22} className="shrink-0" /></a>
               ))}
             </div>

             {/* Divider — intentional switch from centered identity to left-aligned details */}
             <div className="my-7 border-t border-black/5 dark:border-white/5" />

             {/* Details — left-aligned, one consistent axis */}
             <div className="space-y-2">
               {/* Email — interactive copy chip (bordered surface + hover "Copy" hint) */}
               <button
                 onClick={handleCopyEmail}
                 className="group flex w-full items-center gap-3 rounded-xl border border-black/5 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] px-3.5 py-2.5 text-left transition-colors hover:border-black/10 dark:hover:border-white/20 hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
               >
                 {emailCopied ? (
                   <Check size={16} className="shrink-0 text-zinc-900 dark:text-white" />
                 ) : (
                   <Mail size={16} className="shrink-0 text-zinc-500 dark:text-zinc-400" />
                 )}
                 <span className="min-w-0 flex-1 truncate text-xs md:text-sm font-light text-zinc-600 dark:text-zinc-300">
                   {emailCopied ? <span className="font-medium text-zinc-900 dark:text-white">Copied!</span> : profile.email}
                 </span>
                 {!emailCopied && (
                   <span className="shrink-0 text-[10px] font-medium uppercase tracking-[0.15em] text-zinc-400 dark:text-zinc-500 opacity-0 transition-opacity group-hover:opacity-100">
                     Copy
                   </span>
                 )}
               </button>
               {/* Location — static row, aligned to the same left axis (transparent border matches chip padding) */}
               <div className="flex items-center gap-3 border border-transparent px-3.5 py-1 text-xs md:text-sm font-light text-zinc-600 dark:text-zinc-300">
                  <MapPin size={16} className="shrink-0 text-zinc-500 dark:text-zinc-400" /> {profile.location}
               </div>
             </div>
          </GlassCard>

          {/* Strategic Partnerships Card */}
          <GlassCard hoverScale={false} className="p-6 md:p-8 flex items-center justify-center min-h-[160px] transform-gpu will-change-transform backface-hidden">
             <div className="text-center w-full flex flex-col gap-6 justify-center">
               <h3 className="text-xl md:text-2xl text-zinc-900 dark:text-white font-light">
                 {profile.investmentCard?.title || "Strategic Opportunities"}
               </h3>
               <button 
                 onClick={handleInvestmentAction}
                 className="w-full px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-full font-medium text-sm hover:scale-[1.02] transition-transform inline-block shadow-lg [.nardogrey_&]:bg-white [.nardogrey_&]:text-zinc-900"
               >
                 {profile.investmentCard?.buttonText || "Start a Conversation"}
               </button>
             </div>
          </GlassCard>
        </div>

        {/* Right Column (Content) */}
        <div className="lg:col-span-8 space-y-6 md:space-y-8">
          
          {/* --- BIOGRAPHY SECTION (SCROLL WITH FADE) --- */}
          {/* 1. overflow-hidden на родителе, чтобы маска не резала скругления карточки.
             2. Внутренний div имеет overflow-y-auto (скролл) и maskImage (градиент).
          */}
          <GlassCard hoverScale={false} className="relative p-6 md:p-10 h-[350px] md:h-[400px] flex flex-col group overflow-hidden">
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center mb-4 z-10 flex-shrink-0">
                 <h3 className="text-lg md:text-xl font-light text-zinc-900 dark:text-white">Biography</h3>
                 
                 <button 
                   onClick={() => setIsBioOpen(true)}
                   className="p-2 rounded-full text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                   title="Expand biography"
                 >
                   <Maximize2 size={18} />
                 </button>
              </div>
              
              <div
                data-lenis-prevent
                className="relative flex-1 overflow-y-auto custom-scrollbar pr-2"
                style={{
                  // Градиентная маска: сверху видно всё (black), внизу прозрачно (transparent)
                  // Скролл работает ВНУТРИ этой маски.
                  maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)'
                }}
              >
                {/* pb-20 (большой отступ) позволяет прокрутить текст так, чтобы последние строки вышли из "зоны затемнения" */}
                <div className="space-y-6 pb-20">
                  <p className="text-zinc-600 dark:text-zinc-300 leading-7 md:leading-8 font-light text-base md:text-lg whitespace-pre-line">
                    {fullBiography}
                  </p>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* --- EXPERIENCE SECTION --- */}
           <GlassCard hoverScale={false} className="p-6 md:p-8 transform-gpu will-change-transform backface-hidden">
            <h3 className="text-lg md:text-xl font-light text-zinc-900 dark:text-white mb-6">
              Experience
            </h3>
            {profile.experience && profile.experience.length > 0 ? (
                <div className="flex flex-col">
                  {profile.experience.map((exp, idx) => {
                    const period = exp.period || [exp.startDate, exp.endDate].filter(Boolean).join(' - ');
                    const isCurrent = /present|now|current/i.test(period || exp.endDate || '');
                    return (
                      <div
                        key={idx}
                        className="group flex items-start gap-4 sm:gap-5 py-6 border-t border-black/5 dark:border-white/5 first:border-t-0 first:pt-0"
                      >
                        {/* Framed logo tile — square 1:1 app-icon fills flush edge-to-edge (rounded corners via overflow-hidden); smaller on mobile (44px) to give the text column room */}
                        <div className="w-11 h-11 sm:w-14 sm:h-14 flex-shrink-0 rounded-xl bg-white dark:bg-white/5 ring-1 ring-black/10 dark:ring-white/10 flex items-center justify-center overflow-hidden shadow-sm">
                            {exp.logoUrl ? (
                              <img
                                src={exp.logoUrl}
                                alt={exp.company}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Briefcase size={22} className="text-zinc-400" />
                            )}
                        </div>

                        <div className="flex-1 min-w-0 pt-0.5">
                          {/* Primary — company (semibold) + right-anchored year index, baseline aligned */}
                          <div className="flex items-baseline justify-between gap-4">
                            <div className="flex items-center gap-2 min-w-0">
                              <h4 className="text-base md:text-lg font-semibold tracking-tight text-zinc-900 dark:text-white leading-tight">
                                {exp.company}
                              </h4>
                              {isCurrent && (
                                <span className="shrink-0 text-[10px] font-medium uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400 border border-black/10 dark:border-white/15 rounded-full px-2 py-0.5">
                                  Current
                                </span>
                              )}
                              <a href={(exp as any).website || '#'} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <ExternalLink size={14} />
                              </a>
                            </div>
                            {period && (
                              <span className="shrink-0 hidden sm:inline text-xs tabular-nums tracking-wide text-zinc-400 [.nardogrey_&]:text-zinc-300 whitespace-nowrap">
                                {period}
                              </span>
                            )}
                          </div>

                          {/* Secondary — role */}
                          {exp.role && (
                            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
                              {exp.role}
                            </p>
                          )}

                          {/* Tertiary — quiet meta line; location only (year is the desktop right-anchored index / mobile bottom line) */}
                          {(exp.location || period) && (
                            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-400">
                              {exp.location}
                            </p>
                          )}

                          {/* Supplementary description */}
                          {exp.description && (
                            <p className="mt-2 text-sm font-light leading-relaxed text-zinc-600 dark:text-zinc-400">
                              {exp.description}
                            </p>
                          )}

                          {/* Mobile-only bottom year line — right-anchored, quietly closes the entry (echoes the desktop right-anchored index) */}
                          {period && (
                            <p className="sm:hidden mt-2 text-right text-[11px] tracking-[0.15em] tabular-nums text-zinc-400 [.nardogrey_&]:text-zinc-300">
                              {period}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
            ) : (
               <p className="text-zinc-500 text-sm italic p-2">No experience listed.</p>
            )}
          </GlassCard>

          {/* --- EDUCATION SECTION --- */}
          <GlassCard hoverScale={false} className="p-6 md:p-8 transform-gpu will-change-transform backface-hidden">
            <h3 className="text-lg md:text-xl font-light text-zinc-900 dark:text-white mb-6">
              Education
            </h3>
            {profile.education.length > 0 ? (
                <div className="flex flex-col">
                  {profile.education.map((edu, idx) => {
                    const years = edu.year || [edu.startDate, edu.endDate].filter(Boolean).join(' - ');
                    return (
                      <div
                        key={idx}
                        className="group flex items-start gap-4 sm:gap-5 py-6 border-t border-black/5 dark:border-white/5 first:border-t-0 first:pt-0"
                      >
                        {/* Framed logo tile — square 1:1 app-icon fills flush edge-to-edge (rounded corners via overflow-hidden); smaller on mobile (44px) to give the text column room */}
                        <div className="w-11 h-11 sm:w-14 sm:h-14 flex-shrink-0 rounded-xl bg-white dark:bg-white/5 ring-1 ring-black/10 dark:ring-white/10 flex items-center justify-center overflow-hidden shadow-sm">
                            {edu.logoUrl ? (
                              <img src={edu.logoUrl} alt={edu.school} className="w-full h-full object-cover" />
                            ) : (
                              <GraduationCap size={22} className="text-zinc-400" />
                            )}
                        </div>

                        <div className="flex-1 min-w-0 pt-0.5">
                          {/* Primary — school (semibold) + right-anchored year index, baseline aligned */}
                          <div className="flex items-baseline justify-between gap-4">
                            <div className="flex items-center gap-2 min-w-0">
                              <h4 className="text-base md:text-lg font-semibold tracking-tight text-zinc-900 dark:text-white leading-tight">
                                {edu.school}
                              </h4>
                              <a href={(edu as any).website || '#'} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <ExternalLink size={14} />
                              </a>
                            </div>
                            {years && (
                              <span className="shrink-0 hidden sm:inline text-xs tabular-nums tracking-wide text-zinc-400 [.nardogrey_&]:text-zinc-300 whitespace-nowrap">
                                {years}
                              </span>
                            )}
                          </div>

                          {/* Secondary — degree · field as one prose line (no labels) */}
                          {(edu.degree || edu.fieldOfStudy) && (
                            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
                              {[edu.degree, edu.fieldOfStudy].filter(Boolean).join(' · ')}
                            </p>
                          )}

                          {/* Tertiary — quiet meta line: location · grade (year is the desktop right-anchored index / mobile bottom line) */}
                          {(years || edu.location || edu.grade) && (
                            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-400">
                              {[edu.location, edu.grade].filter(Boolean).join(' · ')}
                            </p>
                          )}

                          {/* Mobile-only bottom year line — right-anchored, quietly closes the entry (echoes the desktop right-anchored index) */}
                          {years && (
                            <p className="sm:hidden mt-2 text-right text-[11px] tracking-[0.15em] tabular-nums text-zinc-400 [.nardogrey_&]:text-zinc-300">
                              {years}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
            ) : (
               <p className="text-zinc-500 text-sm italic p-2">No education history listed.</p>
            )}
          </GlassCard>

          {/* Core Competencies — grouped chips: quiet category labels over rows of
              pills; signature skills weighted as solid white. Two-column category
              grid on desktop, stacks on mobile. */}
          <GlassCard hoverScale={false} className="p-6 md:p-8 transform-gpu will-change-transform backface-hidden">
            <h3 className="text-lg md:text-xl font-light text-zinc-900 dark:text-white mb-6">Core Competencies</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
              {COMPETENCY_GROUPS.map(({ label, skills, fullWidth }) => (
                <div key={label} className={fullWidth ? 'sm:col-span-2' : undefined}>
                  <h4 className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 [.nardogrey_&]:text-zinc-300 mb-4">
                    {label}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <span key={skill} className={SIGNATURE_SKILLS.has(skill.trim()) ? SIGNATURE_PILL : QUIET_PILL}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

        </div>
      </div>

      {/* --- MODAL FOR FULL BIOGRAPHY --- */}
      {isBioOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity" 
            onClick={() => setIsBioOpen(false)}
          />
          <div className="relative w-full max-w-3xl max-h-[85vh] p-0 flex flex-col animate-in zoom-in-95 duration-200 z-10 shadow-2xl border-white/20 bg-[#09090b] dark:bg-[#09090b] transform-gpu backface-hidden overflow-hidden rounded-3xl">
            
            {/* Close Button */}
            <button 
              onClick={() => setIsBioOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 transition-colors z-50"
            >
              <X size={20} />
            </button>

            {/* Modal Header */}
            <div className="p-6 md:p-8 border-b border-white/10 bg-white/5">
              <h2 className="text-2xl md:text-3xl font-light text-white">Full Biography</h2>
              <p className="text-zinc-400 mt-2 text-sm">The story behind the ventures.</p>
            </div>

            {/* Modal Body — data-lenis-prevent keeps this scrollable while the
                scroll lock has stopped Lenis. */}
            <div data-lenis-prevent className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
              <div className="prose prose-invert max-w-none">
                <p className="text-base md:text-lg text-zinc-300 leading-relaxed whitespace-pre-line font-light">
                  {fullBiography}
                </p>
              </div>
              <div className="h-10" />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
